import React, { ReactElement, useState } from 'react';
import useSWR from 'swr';
import { GetStaticPropsResult } from 'next';
import { useRouter } from 'next/router';
import styled from '@emotion/styled';
import { MEDIA_QUERY } from '@bucketplace/design-system';

import { PromotionResponse } from 'promotions/types';
import { PromotionsTemplate } from 'promotions/components/PromotionsTemplate';
import { Error } from 'components/Error';
import { API_GATEWAY_HOST } from 'common';
import { serverHttp } from 'common/http';

import { usePromotionQuery } from 'promotions/hooks/usePromotionQuery';
import FeedHead from 'components/Layout/FeedHead';

const isProd = process.env.APP_PROFILE === 'prod';
// dev 환경에서는 12431 사용
const PROMOTION_ID = isProd ? 16423 : 12522;
const EVENT_API_PATH = '/api/promotions';
const IMAGE_URL = 'https://prs.ohousecdn.com/apne2/commerce/big-promotion/v1-434814487736384.png';
const TITLE = '오늘의집 집요한 BLACK FRIDAY';
const DESCRIPTION = '1년에 단 한 번, 연중 최대 세일';

export interface BigPromotionPageProps {
  data: PromotionResponse;
}

export default function BigPromotionPage ({
  data: promotionData,
}: BigPromotionPageProps): ReactElement {
  const [isEditorLoaded, setIsEditorLoaded] = useState(false);
  const router = useRouter();

  const {
    promotionId,
    anchorSection,
    anchorTargetId,
  } = usePromotionQuery({ isProd, defaultId: PROMOTION_ID });

  const { data: csrPromotionData, error: promotionError } = useSWR<PromotionResponse>(
    `${EVENT_API_PATH}/${promotionId}`,
    {
      isPaused: () => !!promotionData,
    }
  );

  promotionData = promotionData ?? csrPromotionData;
  const isLoading = !promotionData && !promotionError || !isEditorLoaded;
  const isInitialAnchor = anchorSection === 'true';

  if (promotionData?.['statusCode'] >= 400) {
    return <Error statusCode={promotionData['statusCode']} />;
  }


  if (!router.isReady || !promotionData?.exhibition) {
    return (
      <FeedHead
        title={TITLE}
        description={DESCRIPTION}
        ogTitle={TITLE}
        ogDescription={DESCRIPTION}
        ogType="website"
        image={IMAGE_URL}
      />
    );
  }
  const promotion = promotionData.exhibition;
  const { units, id: detailId } = promotion.details[0];

  return (
    <>
      <FeedHead
        title={TITLE}
        description={DESCRIPTION}
        ogTitle={TITLE}
        ogDescription={DESCRIPTION}
        ogType="website"
        image={IMAGE_URL}
      />
      <Template
        units={units}
        promotionId={promotion.id}
        title={promotion.title}
        coverImageUrls={promotion.coverImageUrls}
        detailId={detailId}
        anchorInfo={{
          targetId: anchorTargetId,
          isInitialAnchor,
        }}
        endDate={promotionData.exhibition.endAt}
        isLoading={isLoading}
        onViewerLoaded={(isLoaded: boolean) => {
          setIsEditorLoaded(isLoaded);
        }}
      />
    </>
  );
}

export const getStaticProps = async (): Promise<GetStaticPropsResult<any>> => {
  const isBuildPhase = process.env.NEXT_PHASE === 'phase-production-build';
  if (isBuildPhase) {
    return {
      props: {
        data: null,
      },
      revalidate: 1,
    };
  }

  const json = await serverHttp.get(`${API_GATEWAY_HOST}/exhibition/gateway/exhibition/user/v1/get?id=${PROMOTION_ID}`).json();

  return {
    props: {
      data: json,
    },
    revalidate: 1,
  };
};

const Template = styled(PromotionsTemplate)`
  padding: 0 60px;
  ${MEDIA_QUERY.max('tablet', `
    padding: 0;
  `)}
`;
